import {
	MAX_RANGE_DAYS,
	MINUTE,
	type Billet,
	type Interval,
	type NavigationEvent,
	type NavigatorKind,
	type TimeRange,
	type TimelineData,
} from "./models";

export function rangeError(range: TimeRange): string | null {
	if (
		!Number.isFinite(range.start) ||
		!Number.isFinite(range.end) ||
		range.end <= range.start
	)
		return "Choose an end time after the start time.";
	if (range.end - range.start > MAX_RANGE_DAYS * 24 * 60 * MINUTE)
		return `Choose a range of ${MAX_RANGE_DAYS} days or less.`;
	return null;
}

/** The shared API converts datetime2 through .NET's local DateTime converter.
 * Send UTC clock fields without a suffix to avoid its Z-to-server-local conversion.
 * SQL interprets these datetime2 values as UTC; responses carry an explicit Z.
 */
export const utcSqlTimestamp = (time: number) =>
	new Date(time).toISOString().slice(0, -1);

export function navigationEvents(
	data: TimelineData,
	range: TimeRange,
): Record<NavigatorKind, NavigationEvent[]> {
	const within = (time: number) => time >= range.start && time < range.end;
	return {
		campaign: data.campaigns
			.filter((e) => within(e.start))
			.map((e) => ({ kind: "campaign", id: e.Id, time: e.start })),
		workOrder: data.workOrders
			.filter((e) => within(e.start))
			.map((e) => ({ kind: "workOrder", id: e.Id, time: e.start })),
		cobble: data.billets
			.filter((e) => e.IsGood === false && within(e.start))
			.map((e) => ({ kind: "billet", id: e.Id, time: e.start })),
		stop: data.stops.map((e) => ({
			kind: "stop",
			id: e.Id,
			time: Math.max(e.start, range.start),
		})),
		roll: data.rolls.map((e) => ({ kind: "roll", id: e.Id, time: e.start })),
	};
}

/** A selected index, rather than a timestamp, preserves simultaneous events. */
export function findOccurrence(
	events: NavigationEvent[],
	time: number,
	direction: -1 | 1,
	current: number | null,
): number | null {
	if (current !== null) {
		const next = current + direction;
		return next >= 0 && next < events.length ? next : null;
	}
	if (direction === 1) {
		const next = events.findIndex((e) => e.time >= time);
		return next < 0 ? null : next;
	}
	for (let i = events.length - 1; i >= 0; i--)
		if (events[i].time <= time) return i;
	return null;
}

export interface PackedInterval<T> {
	item: T;
	top: number;
	height: number;
	lane: number;
	lanes: number;
}
/** Pack the visual footprint, including labels for instantaneous/missing observations. */
export function packIntervals<T extends Interval>(
	items: T[],
	range: TimeRange,
	pixelsPerMinute: number,
	minimumHeight = 30,
): PackedInterval<T>[] {
	const scale = pixelsPerMinute / MINUTE;
	const packed: PackedInterval<T>[] = [];
	const laneEnds: number[] = [];
	let clusterStart = 0;
	let clusterEnd = -Infinity;
	let clusterLanes = 0;
	const finishCluster = () => {
		for (let i = clusterStart; i < packed.length; i++)
			packed[i].lanes = clusterLanes;
		clusterStart = packed.length;
		laneEnds.length = 0;
		clusterLanes = 0;
	};
	for (const item of [...items].sort(
		(a, b) => a.start - b.start || a.Id - b.Id,
	)) {
		const end =
			item.end !== null && item.end >= item.start ? item.end : item.start;
		if (
			item.start >= range.end ||
			end < range.start ||
			(end === range.start && item.start < range.start)
		)
			continue;
		const top = Math.max(0, (item.start - range.start) * scale);
		const height = Math.max(
			minimumHeight,
			(Math.min(end, range.end) - Math.max(item.start, range.start)) * scale,
		);
		if (top >= clusterEnd) finishCluster();
		let lane = laneEnds.findIndex((value) => value <= top);
		if (lane < 0) lane = laneEnds.length;
		laneEnds[lane] = top + height + 3;
		clusterLanes = Math.max(clusterLanes, lane + 1);
		clusterEnd = Math.max(clusterEnd, top + height + 3);
		packed.push({ item, top, height, lane, lanes: 1 });
	}
	finishCluster();
	return packed;
}

export function billetObservedEnd(billet: Billet): number | null {
	if (billet.end !== null && billet.end >= billet.start) return billet.end;
	if (billet.lastObservedEnd != null && billet.lastObservedEnd >= billet.start)
		return billet.lastObservedEnd;
	const ends = billet.passages
		.filter((p) => p.end >= p.start)
		.map((p) => p.end);
	return ends.length ? Math.max(billet.start, ...ends) : null;
}

export const timeText = (time: number) =>
	new Date(time).toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	});
export const dateTimeText = (time: number | null) =>
	time === null ? "Missing" : new Date(time).toLocaleString();
export const numberText = (value: number | null | undefined) =>
	value == null
		? "—"
		: value.toLocaleString(undefined, { maximumFractionDigits: 1 });
export function durationText(start: number, end: number | null): string {
	if (end === null || end < start) return "Not measured";
	const seconds = Math.floor((end - start) / 1000);
	return seconds >= 3600
		? `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
		: `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export interface SummaryBucket {
	start: number;
	end: number;
	billets: number;
	cobbles: number;
	stopMinutes: number;
	rolls: number;
}
export function summarize(
	data: TimelineData,
	range: TimeRange,
): SummaryBucket[] {
	const step = Math.max(
		15 * MINUTE,
		Math.ceil((range.end - range.start) / (96 * MINUTE)) * MINUTE,
	);
	const buckets: SummaryBucket[] = [];
	for (let start = range.start; start < range.end; start += step)
		buckets.push({
			start,
			end: Math.min(start + step, range.end),
			billets: 0,
			cobbles: 0,
			stopMinutes: 0,
			rolls: 0,
		});
	for (const b of data.billets) {
		const bucket = buckets[Math.floor((b.start - range.start) / step)];
		if (bucket) {
			bucket.billets++;
			if (b.IsGood === false) bucket.cobbles++;
		}
	}
	for (const r of data.rolls) {
		const bucket = buckets[Math.floor((r.start - range.start) / step)];
		if (bucket) bucket.rolls++;
	}
	// Merge overlapping stop intervals so the summary never double-counts downtime.
	const merged: { start: number; end: number }[] = [];
	for (const stop of [...data.stops].sort((a, b) => a.start - b.start)) {
		const start = Math.max(range.start, stop.start);
		const end = Math.min(range.end, stop.end ?? Date.now());
		if (end <= start) continue;
		const last = merged[merged.length - 1];
		if (last && start <= last.end) last.end = Math.max(last.end, end);
		else merged.push({ start, end });
	}
	for (const stop of merged)
		for (const b of buckets)
			b.stopMinutes +=
				Math.max(0, Math.min(stop.end, b.end) - Math.max(stop.start, b.start)) /
				MINUTE;
	return buckets;
}
